import { useCallback, useEffect, useMemo, useState } from "react";
import api, { fmtARS, fmtNum } from "@/lib/api";
import DashboardProyeccionPanel from "@/pages/DashboardProyeccionPanel";
import Topbar, { CotizacionWidget } from "@/components/layout/Topbar";
import DateInput from "@/components/DateInput";
import DashboardTabsBar from "@/components/DashboardTabsBar";
import TnVariacionCard from "@/components/TnVariacionCard";
import AuditoriaDesviosCard from "@/components/AuditoriaDesviosCard";
import ProductosVendidosCard from "@/components/ProductosVendidosCard";
import VentasListaIncorrectaCard from "@/components/VentasListaIncorrectaCard";
import FrecuenciaClientesCard from "@/components/FrecuenciaClientesCard";
import PedidosReprogramadosCard from "@/components/PedidosReprogramadosCard";
import InsumosCompradosCard from "@/components/InsumosCompradosCard";
import PosicionNetaCard from "@/components/PosicionNetaCard";
import { useCurrency } from "@/context/CurrencyContext";
import { TrendUp, TrendDown, Coins, Wallet, Warehouse, Package, Bank, CaretRight, X, Scissors, TrashSimple, Factory } from "@phosphor-icons/react";

const PRESETS = [
  { key: "esta_semana", label: "Esta semana" },
  { key: "semana_anterior", label: "Semana anterior" },
  { key: "este_mes", label: "Este mes" },
  { key: "mes_anterior", label: "Mes anterior" },
  { key: "otro", label: "Otro…" },
];

function rangoDelPreset(key) {
  const hoy = new Date();
  const d = (dt) => dt.toISOString().slice(0, 10);
  const start = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  // Semana ISO: lunes → domingo
  const lunes = (dt) => {
    const x = start(dt);
    const wd = (x.getDay() + 6) % 7; // lunes=0, domingo=6
    x.setDate(x.getDate() - wd);
    return x;
  };
  if (key === "esta_semana") {
    const l = lunes(hoy);
    const dom = new Date(l); dom.setDate(l.getDate() + 6);
    return { desde: d(l), hasta: d(dom) };
  }
  if (key === "semana_anterior") {
    const l = lunes(hoy);
    const domAnt = new Date(l); domAnt.setDate(l.getDate() - 1);
    const lunAnt = new Date(domAnt); lunAnt.setDate(domAnt.getDate() - 6);
    return { desde: d(lunAnt), hasta: d(domAnt) };
  }
  if (key === "este_mes") {
    const p = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const u = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return { desde: d(p), hasta: d(u) };
  }
  if (key === "mes_anterior") {
    const p = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const u = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    return { desde: d(p), hasta: d(u) };
  }
  return { desde: d(hoy), hasta: d(hoy) };
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState("este_mes");
  const initial = useMemo(() => rangoDelPreset("este_mes"), []);
  const [desde, setDesde] = useState(initial.desde);
  const [hasta, setHasta] = useState(initial.hasta);
  const { currency, cotizacion } = useCurrency();
  const isUSD = currency === "USD";
  // Tabs + widget catalog: pestañas personalizables por usuario.
  const [tabs, setTabs] = useState([]);
  const [tabsCanEdit, setTabsCanEdit] = useState(true);
  const [activeTabId, setActiveTabId] = useState(null);
  const [widgetCatalog, setWidgetCatalog] = useState([]);
  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];
  const visibleKeys = new Set(activeTab?.widget_keys || []);
  const show = (key) => visibleKeys.size === 0 || visibleKeys.has(key);

  const cargarTabs = useCallback(async (preferId = null) => {
    try {
      const [tabsRes, catRes] = await Promise.all([
        api.get("/dashboard/tabs"),
        api.get("/dashboard/widget-catalog"),
      ]);
      const list = tabsRes.data?.tabs || [];
      setTabs(list);
      setTabsCanEdit(tabsRes.data?.can_edit !== false);
      setWidgetCatalog(catRes.data?.widgets || []);
      setActiveTabId((cur) => {
        if (preferId && list.some((t) => t.id === preferId)) return preferId;
        if (cur && list.some((t) => t.id === cur)) return cur;
        return list[0]?.id || null;
      });
    } catch (_) {}
  }, []);

  useEffect(() => { cargarTabs(); }, [cargarTabs]);

  // Detalle de KPI (drilldown al pie del dashboard)
  const [selectedKpi, setSelectedKpi] = useState(null); // { kpi, label }
  const [detalle, setDetalle] = useState(null); // { columns, rows, total_ars, total_usd }
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  // fmtMoney convierte un valor en ARS a la moneda de display.
  // Cuando el usuario cambia a USD, todos los valores ARS se dividen por la
  // cotización oficial venta de dolarhoy.com y se prefijan con "US$".
  const fmtMoney = useCallback((arsValue) => {
    const v = Number(arsValue || 0);
    if (isUSD) {
      const rate = Number(cotizacion || 0);
      if (!rate) return "US$ —";
      return `US$ ${fmtNum(v / rate, 2)}`;
    }
    return fmtARS(v);
  }, [isUSD, cotizacion]);

  const cargar = useCallback(async (desdeIn, hastaIn) => {
    try {
      const { data } = await api.get("/dashboard", { params: { fecha_desde: desdeIn, fecha_hasta: hastaIn } });
      setData(data);
    } catch (_) {}
  }, []);

  useEffect(() => { cargar(desde, hasta); }, [cargar, desde, hasta]);

  // Detalle: (re)cargar cuando cambia KPI seleccionado o el rango de fechas.
  useEffect(() => {
    if (!selectedKpi) { setDetalle(null); return; }
    let cancel = false;
    setLoadingDetalle(true);
    api.get(`/dashboard/detalle/${selectedKpi.kpi}`, { params: { fecha_desde: desde, fecha_hasta: hasta } })
      .then(({ data }) => { if (!cancel) setDetalle(data); })
      .catch(() => { if (!cancel) setDetalle(null); })
      .finally(() => { if (!cancel) setLoadingDetalle(false); });
    return () => { cancel = true; };
  }, [selectedKpi, desde, hasta]);

  // Al cambiar el rango, si hay un detalle abierto, recargar.
  useEffect(() => {
    if (selectedKpi) {
      // Scroll a la sección de detalle cuando cambia
      setTimeout(() => {
        const el = document.getElementById("dashboard-detalle-panel");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKpi?.kpi]);

  const aplicarPreset = (key) => {
    setPreset(key);
    if (key === "otro") return; // el usuario elige fechas
    const r = rangoDelPreset(key);
    setDesde(r.desde);
    setHasta(r.hasta);
  };

  // KPIs cuyo drill-down expresa el valor en KILOS (no ARS/USD). Los usamos
  // para saber cuándo formatear como "X kg" en el panel de detalle y en el
  // resumen del header del panel.
  const KG_KPIS = ["kilos_procesados", "merma_transformaciones", "merma_inventario"];
  const isKgKpi = KG_KPIS.includes(selectedKpi?.kpi);
  const fmtKg = (v) => `${fmtNum(Number(v || 0), 3)} kg`;

  const KPI = ({ label, value, sub, icon: Icon, tone = "neutral", testid, drillKpi = null }) => {
    const clickable = !!drillKpi;
    const isSelected = clickable && selectedKpi?.kpi === drillKpi;
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={clickable ? () => {
          setSelectedKpi(isSelected ? null : { kpi: drillKpi, label });
        } : undefined}
        disabled={!clickable}
        className={`border ${isSelected ? "border-[#6B1F24] ring-2 ring-[#6B1F24]/20" : "border-[#E5E5E5]"} bg-white p-5 rounded-sm text-left transition ${clickable ? "hover:border-[#6B1F24] hover:shadow-sm cursor-pointer" : "cursor-default"} w-full`}
      >
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono">{label}</div>
              {clickable && (
                <CaretRight size={10} weight="bold" className={`transition ${isSelected ? "text-[#6B1F24] rotate-90" : "text-neutral-400"}`} />
              )}
            </div>
            <div className="font-mono text-2xl font-semibold text-[#1A1A1A] mt-2">{value}</div>
            {sub && <div className="font-mono text-[11px] text-[#666] mt-1 truncate">{sub}</div>}
          </div>
          <div
            className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ml-2 ${
              tone === "pos" ? "bg-emerald-50 text-emerald-700" :
              tone === "neg" ? "bg-red-50 text-red-700" :
              "bg-[#F5F1E9] text-[#6B1F24]"
            }`}
          >
            <Icon size={18} weight="bold" />
          </div>
        </div>
      </button>
    );
  };

  return (
    <>
      <Topbar
        title="Dashboard"
        subtitle="Resumen del período — ventas, cobros, cajas y cuentas."
        left={(
          <DashboardTabsBar
            tabs={tabs}
            activeId={activeTabId}
            catalog={widgetCatalog}
            onChange={setActiveTabId}
            onReload={(preferId) => cargarTabs(preferId || activeTabId)}
            canEdit={tabsCanEdit}
          />
        )}
      />
      <div className="p-4 space-y-6">

        {/* Filtros de fecha */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm p-3 flex flex-wrap items-center gap-2" data-testid="dashboard-filtros">
          <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono mr-2">Período:</div>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => aplicarPreset(p.key)}
              data-testid={`dashboard-preset-${p.key}`}
              className={`px-3 h-8 rounded-md text-[12px] font-medium transition border ${
                preset === p.key
                  ? "bg-[#6B1F24] border-[#6B1F24] text-white shadow-sm"
                  : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="mx-2 h-6 border-l border-neutral-300"></div>
          <div className="flex items-center gap-2 text-[12px]">
            <span className="text-[11px] uppercase font-mono text-neutral-500">Desde</span>
            <DateInput
              value={desde}
              onChange={(e) => { setPreset("otro"); setDesde(e.target.value); }}
              className="h-8 border border-neutral-300 rounded px-2 text-[12px] font-mono"
              data-testid="dashboard-fecha-desde"
            />
            <span className="text-[11px] uppercase font-mono text-neutral-500">Hasta</span>
            <DateInput
              value={hasta}
              min={desde || undefined}
              onChange={(e) => { setPreset("otro"); setHasta(e.target.value); }}
              className="h-8 border border-neutral-300 rounded px-2 text-[12px] font-mono"
              data-testid="dashboard-fecha-hasta"
            />
          </div>
          {/* Widget de cotización + selector de moneda — sólo en Dashboard */}
          <div className="ml-auto">
            <CotizacionWidget />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {show("ventas_mes") && <KPI
            testid="kpi-ventas-mes"
            drillKpi="ventas_mes"
            label="Ventas del Período"
            value={fmtMoney(data?.ventas_mes?.total)}
            sub={
              `${data?.ventas_mes?.count || 0} facturas · ${fmtNum(data?.ventas_mes?.kilos || 0, 1)} kg` +
              ((data?.ncs_mes?.count || 0) > 0
                ? ` · ${data.ncs_mes.count} NC (${fmtMoney(data.ncs_mes.total || 0)} devueltos${(data.ncs_mes.restadas || 0) > 0 ? `, ${fmtMoney(data.ncs_mes.restadas)} restados` : ""})`
                : "")
            }
            icon={TrendUp}
            tone="pos"
          />}
          {show("ventas_mes_por_ug") && (
            <div className="border border-[#E5E5E5] bg-white rounded-sm p-4" data-testid="kpi-ventas-mes-por-ug">
              <div className="flex items-start gap-2">
                <TrendUp size={18} weight="bold" className="text-emerald-700 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Ventas por Unidad de Negocio</div>
                  {(data?.ventas_mes_por_ug || []).length === 0 ? (
                    <div className="text-sm text-neutral-400 italic mt-1">Sin ventas en el período</div>
                  ) : (
                    <div className="mt-1.5 space-y-1">
                      {data.ventas_mes_por_ug.map((r, i) => (
                        <div key={r.unidad_negocio} className="flex items-baseline justify-between gap-2 text-[12px]"
                             data-testid={`kpi-ventas-ug-row-${i}`}>
                          <div className="font-medium truncate">{r.unidad_negocio}</div>
                          <div className="text-right font-mono">
                            <span className="font-bold text-neutral-800">{fmtMoney(r.total)}</span>
                            <span className="text-[10px] text-neutral-500 ml-2">
                              {r.count} fact · {fmtNum(r.kilos, 1)} kg
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {show("compras_mes") && <KPI
            testid="kpi-compras-mes"
            drillKpi="compras_mes"
            label="Compras del Período"
            value={fmtMoney(data?.compras_mes?.total)}
            sub={`${data?.compras_mes?.count || 0} facturas · prov. productos`}
            icon={TrendDown}
            tone="neg"
          />}
          {show("margen_facturacion") && (() => {
            const v = Number(data?.ventas_mes?.total || 0);
            const c = Number(data?.compras_mes?.total || 0);
            const margenPct = v > 0 ? ((v - c) / v) * 100 : null;
            const tone = margenPct == null ? "neutral" : (margenPct >= 0 ? "pos" : "neg");
            return (
              <KPI
                testid="kpi-margen-facturacion"
                label="Margen s/Facturación"
                value={margenPct == null ? "—" : `${margenPct.toFixed(1)}%`}
                sub={
                  v > 0
                    ? `Ventas ${fmtMoney(v)} · Compras ${fmtMoney(c)} · Bruto ${fmtMoney(v - c)}`
                    : "Sin ventas en el período"
                }
                icon={TrendUp}
                tone={tone}
              />
            );
          })()}
          {/* Fila 2: primera columna vacía, luego Servicios e Insumos y Punto de Equilibrio */}
          <div className="hidden lg:block" aria-hidden="true"></div>
          {show("compras_servicios_mes") && <KPI
            testid="kpi-compras-servicios-mes"
            drillKpi="compras_servicios_mes"
            label="Servicios e Insumos"
            value={fmtMoney(data?.compras_servicios_mes?.total)}
            sub={`${data?.compras_servicios_mes?.count || 0} facturas · gastos operativos`}
            icon={Coins}
            tone="neg"
          />}
          {show("punto_equilibrio") && (() => {
            const v = Number(data?.ventas_mes?.total || 0);
            const c = Number(data?.compras_mes?.total || 0);
            const s = Number(data?.compras_servicios_mes?.total || 0);
            // Punto de equilibrio = Servicios e Insumos / margen (fraccional)
            // margen = (V - C) / V
            let pe = null;
            let margenFrac = null;
            if (v > 0) {
              margenFrac = (v - c) / v;
              if (margenFrac > 0.0001) pe = s / margenFrac;
              else if (s === 0) pe = 0;
            }
            return (
              <KPI
                testid="kpi-punto-equilibrio"
                label="Punto de Equilibrio"
                value={pe == null ? "—" : fmtMoney(pe)}
                sub={
                  pe == null
                    ? "Necesita ventas con margen positivo para calcularse"
                    : `Fórmula: servicios / margen · ${fmtMoney(s)} / ${(margenFrac * 100).toFixed(1)}%`
                }
                icon={Wallet}
                tone="neutral"
              />
            );
          })()}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {show("ctas_cobrar") && <KPI
            testid="kpi-ctas-cobrar"
            drillKpi="ctas_cobrar"
            label="Cuentas por Cobrar"
            value={fmtMoney(data?.ctas_cobrar?.total_ars)}
            sub={`USD ${fmtNum(data?.ctas_cobrar?.total_usd || 0)}`}
            icon={Coins}
          />}
          {show("ctas_pagar") && <KPI
            testid="kpi-ctas-pagar"
            drillKpi="ctas_pagar"
            label="Cuentas por Pagar"
            value={fmtMoney(data?.ctas_pagar?.total_ars)}
            sub={(() => {
              const usd = data?.ctas_pagar?.total_usd || 0;
              const chq = data?.ctas_pagar?.cheques_emitidos_count || 0;
              const chqArs = data?.ctas_pagar?.cheques_emitidos_ars || 0;
              const parts = [`USD ${fmtNum(usd)}`];
              if (chq > 0) {
                parts.push(`${chq} cheque${chq !== 1 ? "s" : ""} emit. ${fmtMoney(chqArs)}`);
              }
              return parts.join(" · ");
            })()}
            icon={Wallet}
          />}
          {show("pasivos") && (() => {
            const p = data?.pasivos || { total_ars: 0, total_usd: 0, count: 0 };
            const usdVal = Number(p.total_usd || 0);
            const arsVal = Number(p.total_ars || 0);
            const rate = Number(cotizacion || 0);
            // Total consolidado: deuda ARS + deuda USD convertida a ARS con la
            // cotización del widget del dashboard. fmtMoney lo convierte a la
            // moneda de display (ARS o USD) según preferencia del usuario.
            const totalConsolidadoArs = arsVal + (rate ? usdVal * rate : 0);
            const subParts = [`${p.count || 0} deuda${(p.count || 0) !== 1 ? "s" : ""}`];
            if (arsVal > 0) subParts.push(`ARS ${fmtNum(arsVal, 0)}`);
            if (usdVal > 0) subParts.push(`USD ${fmtNum(usdVal, 2)}${rate ? ` @${fmtNum(rate, 0)}` : ""}`);
            if (usdVal > 0 && !rate) subParts.push("(sin cotización)");
            return (
              <KPI
                testid="kpi-pasivos"
                drillKpi="pasivos"
                label="Pasivos Fin./Fiscales"
                value={fmtMoney(totalConsolidadoArs)}
                sub={subParts.join(" · ")}
                icon={Bank}
                tone={totalConsolidadoArs > 0 ? "neg" : "neutral"}
              />
            );
          })()}
        </div>

        {/* Posición Neta: cajas + stock a costo − ctas. por pagar − pasivos, con tendencia y proyección */}
        {show("posicion_neta") && (
          <PosicionNetaCard fmtMoney={fmtMoney} cotizacion={cotizacion} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {show("kilos_procesados") && <KPI
            testid="kpi-kilos-procesados"
            drillKpi="kilos_procesados"
            label="Kilos Procesados"
            value={fmtKg(data?.kilos_procesados?.kilos)}
            sub={`${data?.kilos_procesados?.count || 0} transformaciones · ${fmtNum(data?.kilos_procesados?.kilos_producidos || 0, 1)} kg producidos`}
            icon={Factory}
            tone="neutral"
          />}
          {show("merma_transformaciones") && <KPI
            testid="kpi-merma-transformaciones"
            drillKpi="merma_transformaciones"
            label="Merma · Transformaciones"
            value={fmtKg(data?.merma_transformaciones?.kilos)}
            sub={(() => {
              const proc = Number(data?.merma_transformaciones?.kilos_procesados || 0);
              const merma = Number(data?.merma_transformaciones?.kilos || 0);
              const pct = proc > 0 ? (merma / proc) * 100 : null;
              return pct == null
                ? `${data?.merma_transformaciones?.count || 0} transformaciones`
                : `${pct.toFixed(2)}% s/procesados · ${data?.merma_transformaciones?.count || 0} despieces`;
            })()}
            icon={Scissors}
            tone="neg"
          />}
          {show("merma_inventario") && <KPI
            testid="kpi-merma-inventario"
            drillKpi="merma_inventario"
            label="Merma · Inventario/Ajustes"
            value={fmtKg(data?.merma_inventario?.kilos)}
            sub={(() => {
              const b = Number(data?.merma_inventario?.kilos_bajas || 0);
              const i = Number(data?.merma_inventario?.kilos_ingresos || 0);
              const n = data?.merma_inventario?.count || 0;
              const parts = [];
              if (b > 0) parts.push(`Bajas ${fmtNum(b, 1)} kg`);
              if (i > 0) parts.push(`Ingresos ${fmtNum(i, 1)} kg`);
              parts.push(`${n} mov.`);
              return parts.join(" · ");
            })()}
            icon={TrashSimple}
            tone={Number(data?.merma_inventario?.kilos || 0) > 0 ? "neg" : "neutral"}
          />}
        </div>

        {/* Variación de preparación TN (widget completo) */}
        {show("tn_variacion") && (
          <TnVariacionCard fechaDesde={desde} fechaHasta={hasta} fmtMoney={fmtMoney} />
        )}

        {/* Desvíos de peso por operador (widget completo) */}
        {show("auditoria_desvios") && (
          <AuditoriaDesviosCard fechaDesde={desde} fechaHasta={hasta} />
        )}

        {/* Productos vendidos con actualización de costos (widget completo) */}
        {show("productos_vendidos") && (
          <ProductosVendidosCard fechaDesde={desde} fechaHasta={hasta} />
        )}

        {/* Ventas con lista de precios incorrecta (widget completo) */}
        {show("ventas_lista_incorrecta") && (
          <VentasListaIncorrectaCard fechaDesde={desde} fechaHasta={hasta} />
        )}

        {/* Frecuencia de compra por cliente (widget completo) */}
        {show("frecuencia_clientes") && (
          <FrecuenciaClientesCard />
        )}

        {/* Pedidos reprogramados con motivos editables (widget completo) */}
        {show("pedidos_reprogramados") && (
          <PedidosReprogramadosCard />
        )}

        {/* Insumos comprados por proveedor (widget completo) */}
        {show("insumos_comprados") && (
          <InsumosCompradosCard fechaDesde={desde} fechaHasta={hasta} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {show("stock_costo") && <KPI
            testid="kpi-stock-costo"
            drillKpi="stock_costo"
            label="Valorización de Stock · Costo"
            value={fmtMoney(data?.stock_valorizacion?.costo)}
            sub={`${fmtNum(data?.stock_valorizacion?.kilos || 0, 1)} kg disponibles · kg × último costo del producto`}
            icon={Warehouse}
            tone="neutral"
          />}
          {show("stock_venta") && <KPI
            testid="kpi-stock-venta"
            drillKpi="stock_venta"
            label="Valorización de Stock · Venta"
            value={fmtMoney(data?.stock_valorizacion?.venta)}
            sub={(() => {
              const c = Number(data?.stock_valorizacion?.costo || 0);
              const v = Number(data?.stock_valorizacion?.venta || 0);
              if (v <= 0) return "Sin productos con precio de venta cargado";
              const marg = ((v - c) / v) * 100;
              return `Margen potencial ${marg.toFixed(1)}% · a precio_actual de cada producto`;
            })()}
            icon={Package}
            tone="pos"
          />}
        </div>

        {show("saldos_cajas") && (
        <div className="border border-[#E5E5E5] bg-white rounded-sm">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
            <h2 className="font-heading font-semibold text-[15px]">Saldos por Caja</h2>
            <div className="text-[11px] text-[#666] font-mono uppercase tracking-widest">
              {data?.cajas?.length || 0} cuentas
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50">
                <tr className="text-left text-[11px] uppercase font-mono text-[#666]">
                  <th className="px-5 py-2">Caja</th>
                  <th className="px-5 py-2">Tipo</th>
                  <th className="px-5 py-2">Moneda</th>
                  <th className="px-5 py-2 text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {(data?.cajas || []).map((c) => {
                  // Convertimos siempre a la moneda de display.
                  // - Si display=ARS: mostramos ARS; USD-cajas se multiplican por cotiz.
                  // - Si display=USD: mostramos US$; ARS-cajas se dividen por cotiz.
                  const rate = Number(cotizacion || 0);
                  let displayVal;
                  if (isUSD) {
                    if (c.moneda === "USD") displayVal = `US$ ${fmtNum(c.saldo || 0)}`;
                    else displayVal = rate ? `US$ ${fmtNum((c.saldo || 0) / rate, 2)}` : "US$ —";
                  } else {
                    if (c.moneda === "USD") displayVal = rate ? fmtARS((c.saldo || 0) * rate) : `US$ ${fmtNum(c.saldo || 0)}`;
                    else displayVal = fmtARS(c.saldo || 0);
                  }
                  return (
                    <tr key={c.id} data-testid={`dash-caja-${c.id}`}>
                      <td className="px-5 py-2 font-medium">{c.nombre}</td>
                      <td className="px-5 py-2 capitalize text-[#666]">{c.tipo}</td>
                      <td className="px-5 py-2 font-mono">{c.moneda}</td>
                      <td className="px-5 py-2 text-right font-mono">{displayVal}</td>
                    </tr>
                  );
                })}
                {(!data?.cajas || data.cajas.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-[#999]">
                      Sin cajas creadas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
        {/* Panel de detalle de KPI seleccionado */}
        {selectedKpi && (
          <div id="dashboard-detalle-panel" className="border border-[#6B1F24]/30 bg-white rounded-sm" data-testid="dashboard-detalle-panel">
            <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-[#F5F1E9]/40">
              <div>
                <h2 className="font-heading font-semibold text-[15px] text-[#6B1F24]">
                  Detalle · {selectedKpi.label}
                </h2>
                <div className="text-[11px] text-[#666] font-mono mt-0.5">
                  {detalle
                    ? (isKgKpi
                        ? `${detalle.rows.length} ítems · Total ${fmtNum(detalle.total_ars || 0, 3)} kg`
                        : `${detalle.rows.length} ítems · Total ARS ${fmtARS(detalle.total_ars || 0)}${detalle.total_usd ? ` · Total USD ${fmtNum(detalle.total_usd, 2)}` : ""}`)
                    : (loadingDetalle ? "Cargando…" : "—")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedKpi(null)}
                className="w-8 h-8 rounded-sm hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                data-testid="cerrar-detalle"
              >
                <X size={16} />
              </button>
            </div>
            {/* Panel de proyección (sólo para KPIs de deuda que deberían bajar) */}
            {(selectedKpi.kpi === "ctas_pagar" || selectedKpi.kpi === "pasivos") && (
              <div className="px-5 py-4 border-b border-[#E5E5E5]">
                <DashboardProyeccionPanel kpi={selectedKpi.kpi} label={selectedKpi.label} cotizacion={cotizacion} />
              </div>
            )}
            <div className="overflow-x-auto max-h-[500px]">
              {loadingDetalle && (
                <div className="text-center text-[13px] text-[#999] py-10">Cargando detalle…</div>
              )}
              {!loadingDetalle && detalle && detalle.rows.length === 0 && (
                <div className="text-center text-[13px] text-[#999] py-10">
                  Sin datos para el período seleccionado.
                </div>
              )}
              {!loadingDetalle && detalle && detalle.rows.length > 0 && (
                <table className="w-full text-[13px] grid-table">
                  <thead className="bg-neutral-50 sticky top-0">
                    <tr className="text-left text-[11px] uppercase font-mono text-[#666]">
                      {detalle.columns.map((c) => (
                        <th key={c.key} className={`px-4 py-2 ${c.key.startsWith("importe") ? "text-right" : ""}`}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.rows.map((r, i) => (
                      <tr key={r.id || i} className="border-t border-neutral-100 hover:bg-neutral-50">
                        {detalle.columns.map((c) => {
                          const val = r[c.key];
                          if (c.key === "importe_ars") {
                            if (isKgKpi) {
                              return <td key={c.key} className="px-4 py-2 text-right font-mono">{val == null ? "—" : `${fmtNum(Number(val), 3)} kg`}</td>;
                            }
                            return <td key={c.key} className="px-4 py-2 text-right font-mono">{val ? fmtMoney(val) : "—"}</td>;
                          }
                          if (c.key === "importe_usd") {
                            return <td key={c.key} className="px-4 py-2 text-right font-mono">{val ? `US$ ${fmtNum(val, 2)}` : "—"}</td>;
                          }
                          if (c.key === "extra" && typeof val === "number") {
                            return <td key={c.key} className="px-4 py-2 font-mono text-right">{fmtNum(val, 1)}</td>;
                          }
                          return <td key={c.key} className="px-4 py-2 truncate max-w-[280px]">{val || "—"}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
